import {ARCHIVED_ANALYSIS_TOOLTIP_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import {isSendToDocumentEnabled} from 'analysis-tool/outputs/OutputsUtil';
import DataMappingModal from 'analysis-tool/structured-data-mapping/DataMappingModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {DataFrameTableWrapperOverflowOption} from 'data-frames/wrapper/DataFrameTableWrapper';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import runModal from 'ui-library/runModal';

import {FAILED_STEP_MESSAGE} from './DataFrameView';
import {REACH_OUT_TO_BENCHLING_TEXT} from './MainViewConstants';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from './MainViewTypes';

const BASE_TIMEOUT_MESSAGE =
  ' timed out. Please try again. If the issue persists, please reach out to Benchling support.';

export function getTransformationErrorText(statusMessage: string | null, stepType: PipelineStepType): string {
  if (statusMessage?.includes('Timed out.')) {
    return `${STEP_TYPE_TO_USER_FACING_NAME[stepType]}${BASE_TIMEOUT_MESSAGE}`;
  }

  const stepStatusMessage = statusMessage ? `Error: ${statusMessage}` : null;
  return `${
    STEP_TYPE_TO_USER_FACING_NAME[stepType]
  } transformation was unsuccessful. Either update its configuration to try again, or remove it. ${
    stepStatusMessage ?? REACH_OUT_TO_BENCHLING_TEXT
  }`;
}

export function getSaveToResultsOverflowOption(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  dataFrameId: string,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void,
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void
): DataFrameTableWrapperOverflowOption | null {
  if (isSendToDocumentEnabled()) {
    return null;
  }

  const outputStepData = getOutputStepDataForDataFrame(step, dataFrameId);
  const savedResultsStepId: string | null =
    analysis.execution.steps.find(
      (step) =>
        step.definition.stepType === PipelineStepType.SAVE_TO_RESULTS &&
        step.inputStepDatas.find((stepData) => stepData.dataFrames.find((df) => df.id === dataFrameId)) !=
          null
    )?.id ?? null;

  if (savedResultsStepId != null && isFeatureEnabled({feature: ResultsFeature.RESULTS_OPEN})) {
    return {
      icon: 'table',
      title: 'View saved Results',
      onSelect: () => {
        onChangeSelectedItem({
          type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT,
          stepId: savedResultsStepId,
        });
      },
    };
  } else if (
    savedResultsStepId == null &&
    (analysis.permissions.writable || analysis.permissions.unarchivable) &&
    isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE})
  ) {
    let isDisabled = false;
    let tooltipTitle;
    if (isArchived(analysis)) {
      isDisabled = true;
      tooltipTitle = ARCHIVED_ANALYSIS_TOOLTIP_TEXT;
    } else if (step.statusCode === PipelineStepStatusCode.FAILED) {
      isDisabled = true;
      tooltipTitle = FAILED_STEP_MESSAGE;
    }

    return {
      icon: 'table',
      title: 'Save table to Results',
      isDisabled,
      tooltipTitle,
      onSelect: () =>
        runModal(DataMappingModal, {
          analysisId: analysis.id,
          inputDataReference: {stepId: step.id, stepDataName: outputStepData!.name},
          dataFrameId,
          onOpenStepInitializationView,
        }),
    };
  }
  return null;
}

export function isRegressionStepIncomplete(step: GetAnalysisPipelineStep): boolean {
  assert(
    step.definition.stepType === PipelineStepType.REGRESSION,
    'Expected this function to only be called on a regression step.'
  );

  const maybeRegressionConfig = step.config?.variables.find(
    (variable) => variable.name === 'regressionConfig'
  );

  return maybeRegressionConfig == null;
}

export function getRegressionErrorText(step: GetAnalysisPipelineStep): string | null {
  assert(
    step.definition.stepType === PipelineStepType.REGRESSION,
    'Expected this function to only be called on a regression step.'
  );

  // Handle input validation and service error messages (see: `services/interactive-analysis/common/regression/regression.py`)
  if (
    step.statusMessage?.includes('Data cannot contain NaNs or Infs for curve fitting.') ||
    step.statusMessage?.includes('Column does not have a valid type')
  ) {
    return 'Non-numeric or infinite values included in regression data.';
  } else if (
    step.statusMessage?.includes('Failed to optimize parameters. Data is insufficient to specify curve fit.')
  ) {
    return 'Could not calculate a valid regression of this type using this data.';
  } else if (step.statusMessage?.includes('Curve fit must have at least 4 data points to specify fit.')) {
    return '4 or more data points are required for this regression model.';
  } else if (
    step.statusMessage?.includes('Curve fit is not specified for data that falls on a horizontal line.') ||
    step.statusMessage?.includes('Curve fit is not specified for data that falls on a vertical line.')
  ) {
    return 'Curve fit cannot be calculated for data in a horizontal or vertical line.';
  } else if (step.statusMessage?.includes('Failed to pass data validation checks.')) {
    return 'X values and Weight must be > 0.';
  } else if (isRegressionStepIncomplete(step)) {
    return 'Regression has not been applied yet.';
  } else if (step.statusMessage?.includes('Timed out.')) {
    return `${STEP_TYPE_TO_USER_FACING_NAME[step.definition.stepType]}${BASE_TIMEOUT_MESSAGE}`;
  }
  return null;
}
